import { createContext, useContext } from 'react';

/**
 * CryptoKeyContext
 *
 * Holds the AES-GCM 256-bit CryptoKey in React memory only — it is NEVER
 * written to localStorage, sessionStorage, or any other persistent store.
 *
 * Shape of context value:
 *   {
 *     cryptoKey:  CryptoKey | null   — the imported WebCrypto key, or null if not yet ready
 *     keyVersion: string | null      — e.g. "v1", used to tag stored ciphertexts for rotation support
 *     isReady:    boolean  — true once the key has been successfully fetched and imported
 *   }
 *
 * The key is fetched from Orchard Core CMS siteSettings via the authenticated
 * GraphQL proxy (api/graphql-proxy) inside App.jsx, immediately after the user
 * is confirmed authenticated. This ensures the key is only ever accessible to
 * logged-in users.
 *
 * Usage:
 *   import { useCryptoKey } from '../Context/CryptoKey/CryptoKeyContext';
 *   const { cryptoKey, keyVersion, isReady } = useCryptoKey();
 */

const CryptoKeyContext = createContext({
  cryptoKey: null,
  keyVersion: null,
  isReady: false,
});

/**
 * useCryptoKey — convenience hook to consume CryptoKeyContext.
 * Must be used inside a component tree wrapped by the CryptoKeyContext.Provider
 * (set up in App.jsx inside <AuthenticatedTemplate>).
 */
export const useCryptoKey = () => useContext(CryptoKeyContext);

export default CryptoKeyContext;
